import { computed, defineComponent, h, readonly, ref, watch } from 'vue'
import { ElTooltip } from 'element-plus'
import { suppressNativeTitles } from './nativeTitlePolicy'
import { observeDisplayState } from './shared/displayState'

const mobileViewport = ref(false)
export const mobileHoverHintsDisabled = readonly(mobileViewport)

// Wrap business tooltips only. Element Plus also uses its original Tooltip for
// Select, Popover and other interactive panels, which must remain available.
export const MobileTooltip = defineComponent({
  name: 'MobileTooltip',
  inheritAttrs: false,
  props: {
    ...ElTooltip.props,
    mobileFeedback: Boolean
  },
  setup(props, { attrs, slots, expose }) {
    const tooltip = ref()
    const mobileTriggers = computed(() => {
      const triggers = Array.isArray(props.trigger) ? props.trigger : [props.trigger]
      return triggers.filter(trigger => trigger !== 'hover' && trigger !== 'focus')
    })
    const disabled = computed(() => props.disabled || (mobileViewport.value &&
      !props.mobileFeedback && typeof props.visible !== 'boolean' && !mobileTriggers.value.length))

    expose({
      get popperRef() { return tooltip.value?.popperRef },
      get contentRef() { return tooltip.value?.contentRef },
      onOpen: (...args) => { if (!disabled.value) tooltip.value?.onOpen(...args) },
      onClose: (...args) => tooltip.value?.onClose(...args),
      hide: (...args) => tooltip.value?.hide(...args),
      updatePopper: (...args) => tooltip.value?.updatePopper(...args),
      isFocusInsideContent: (...args) => tooltip.value?.isFocusInsideContent(...args)
    })

    watch(mobileViewport, () => {
      // Do not carry a desktop hover across a resize/rotation. Controlled
      // validation and explicit action feedback still belong to their caller.
      if (typeof props.visible !== 'boolean') tooltip.value?.hide()
    }, { flush: 'sync' })

    return () => {
      const { mobileFeedback, ...nativeProps } = props
      return h(ElTooltip, {
        ...attrs,
        ...nativeProps,
        ref: tooltip,
        disabled: disabled.value,
        trigger: mobileViewport.value ? mobileTriggers.value : props.trigger,
        triggerKeys: mobileViewport.value && !mobileTriggers.value.length ? [] : props.triggerKeys
      }, slots)
    }
  }
})

export function installMobileHoverHints(app) {
  // Lowercase business tags resolve this alias; library-local ElTooltip
  // registrations continue to resolve the unmodified Element Plus component.
  app.component('el-tooltip', MobileTooltip)
  let restoreTitles
  const observer = observeDisplayState(state => {
    mobileViewport.value = state.layoutTier === 'compact' || (state.isIPad && !state.hasHover)
    if (mobileViewport.value && !restoreTitles) restoreTitles = suppressNativeTitles(document.body)
    else if (!mobileViewport.value && restoreTitles) {
      restoreTitles()
      restoreTitles = undefined
    }
  })
  app.onUnmount(() => {
    observer.stop()
    restoreTitles?.()
    mobileViewport.value = false
  })
}
